import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { FilterOperatorSwitch } from './FilterOperatorSwitch'
import { FilterValueEditor } from './FilterValueEditor'
import { useFilterLabels } from './useFilterLabels'
import type {
  FilterFieldDefinition,
  FilterFieldKey,
  FilterOperator,
  FilterValue,
  TaskFilter,
} from '@/lib/types/filter'
import { FILTER_FIELD_DEFINITIONS } from '@/lib/filters'

interface FilterPillProps {
  filter: TaskFilter
  valueSummary: string
  // All keyed by the chip's id: one property can appear in several OR groups.
  onSetOperator: (filterId: string, operator: FilterOperator) => void
  onToggleValue: (filterId: string, value: FilterValue) => void
  onSetValues: (filterId: string, values: Array<FilterValue>) => void
  onRemove: (filterId: string) => void
  allowedProjectIds?: Array<string>
  /** When true, the remove (X) button is hidden. */
  pinned?: boolean
}

export function FilterPill({
  filter,
  valueSummary,
  onSetOperator,
  onToggleValue,
  onSetValues,
  onRemove,
  allowedProjectIds,
  pinned,
}: FilterPillProps) {
  const { t } = useTranslation('tasks')
  const { fieldLabel } = useFilterLabels()
  // A freshly added filter has no values yet: open its picker straight away
  // so choosing a property flows into choosing values.
  const [editorOpen, setEditorOpen] = useState(() => filter.values.length === 0 && !pinned)
  const fieldDef = (
    FILTER_FIELD_DEFINITIONS as Partial<
      Record<FilterFieldKey, FilterFieldDefinition>
    >
  )[filter.fieldKey]

  const handleToggleValue = useCallback(
    (value: FilterValue) => {
      if (filter.id) onToggleValue(filter.id, value)
    },
    [filter.id, onToggleValue],
  )

  const handleSetValues = useCallback(
    (values: Array<FilterValue>) => {
      if (filter.id) onSetValues(filter.id, values)
    },
    [filter.id, onSetValues],
  )

  if (!fieldDef) return null
  const Icon = fieldDef.icon

  return (
    <div className="flex h-7 items-center overflow-hidden rounded-lg border bg-card text-[12.5px]">
      {/* Icon + field label */}
      <FilterValueEditor
        fieldKey={filter.fieldKey}
        selectedValues={filter.values}
        onToggleValue={handleToggleValue}
        onSetValues={handleSetValues}
        operator={filter.operator}
        allowedProjectIds={allowedProjectIds}
        open={editorOpen}
        onOpenChange={setEditorOpen}
      >
        <button className="flex h-full items-center gap-1.5 px-2 transition-colors hover:bg-accent/50">
          <Icon className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">{fieldLabel(filter.fieldKey)}</span>
        </button>
      </FilterValueEditor>

      {/* Operator */}
      <div className="flex h-full items-center border-l border-border/60 px-1.5">
        <FilterOperatorSwitch
          operator={filter.operator}
          availableOperators={fieldDef.operators}
          onChange={(op) => filter.id && onSetOperator(filter.id, op)}
        />
      </div>

      {/* Value summary — clickable to open editor */}
      <FilterValueEditor
        fieldKey={filter.fieldKey}
        selectedValues={filter.values}
        onToggleValue={handleToggleValue}
        onSetValues={handleSetValues}
        operator={filter.operator}
        allowedProjectIds={allowedProjectIds}
        open={false}
        onOpenChange={setEditorOpen}
      >
        <button
          className="flex h-full items-center border-l border-border/60 px-2 font-medium transition-colors hover:bg-accent/50"
        >
          {valueSummary || t('filters.any')}
        </button>
      </FilterValueEditor>

      {/* Remove button — hidden for pinned filters */}
      {!pinned && (
        <button
          type="button"
          className="flex h-full items-center justify-center border-l border-border/60 px-1.5 transition-colors hover:bg-accent/50"
          onClick={() => filter.id && onRemove(filter.id)}
          aria-label={t('filters.removeFilter', { field: fieldLabel(filter.fieldKey) })}
        >
          <X className="size-3 text-muted-foreground" />
        </button>
      )}
    </div>
  )
}
